import {
	GIGYA_REGISTER,
	GIGYA_INIT_REGISTRATION,
	GIGYA_FINALIZE_REGISTRATION,
	RESEND_VERIFICATION_CODE,
	GIGYA_SET_ACCOUNT_INFO,
} from '../constants/ActionTypes';
import { EVT_COMPLETE_REGISTRATION_ERROR, EVT_REGISTER_ERROR } from '../constants/EventTypes';
import { EMAIL_ALREADY_EXISTS_NOTIFICATION } from '../constants/Notifications';
import { analyticsPush } from '../actions/applicationActions';
import { gigyaGetAccountInfo, videolandLoginFlow, clearRedirectUrl } from './authenticationActions';
import {
	ROUTE_THIRD_PARTY_COOKIES,
	ROUTE_VERIFY_EMAIL,
	ROUTE_ERROR,
	ROUTE_LOGIN,
} from '../constants/Routes';
import {
	THIRD_PARTY_COOKIES_BLOCKED,
	UNVERIFIED_ACCOUNT,
	EMAIL_ALREADY_EXISTS,
} from '../constants/ErrorTypes';
import { EVT_REGISTER_SUCCESS } from '../constants/EventTypes';
import { reportCustomError, reportError } from '../helpers/realUserMonitoring';
import { showNotification } from '../actions/notificationActions';
import history from '../../common/history';

const accountInfoBody = (userData, regToken, redirectUrl, snowplowData) => {
	const body = {
		profile: {},
		data: {
			first_visit_vl: new Date().toISOString(),
			privacy_v_2: true,
			terms_vl: true,
			redirectUrl,
			videolandMigration: {
				bedrock: true,
				migratedDate: new Date().toISOString(),
				smartvod: false,
			},
			snowplow: {
				time_previous_session: Date.now().toString(),
				domain_userid: snowplowData?.domain_userid,
				domain_sessionid: snowplowData?.domain_sessionid,
			},
		},
		regToken,
	};
	if (userData.email) body.email = userData.email;
	if (userData.password) body.password = userData.password;
	if (userData.firstName) body.profile.firstName = userData.firstName;
	if (userData.lastName) body.profile.lastName = userData.lastName;
	if (userData.gender) body.profile.gender = userData.gender;
	if (userData.birthdate) {
		const [birthYear, birthMonth, birthDay] = userData.birthdate
			.split('-')
			.map((part) => parseInt(part, 10));
		body.profile.birthDay = birthDay;
		body.profile.birthMonth = birthMonth;
		body.profile.birthYear = birthYear;
	}
	return Object.assign({}, body, { regSource: 'videoland.com' });
};

export const initRegistration = () => (dispatch) =>
	dispatch({
		type: GIGYA_INIT_REGISTRATION,
		payload: {
			client: 'gigya',
			data: {
				api: 'accounts',
				method: 'initRegistration',
			},
		},
	}).catch((err) => {
		reportError(err);
		throw err;
	});

export const register = (userData, regToken, snowplowDuid) => (dispatch, getState) => {
	const redirectUrl = getState().authentication.redirectUrl;
	const reqBody = accountInfoBody(userData, regToken, redirectUrl, snowplowDuid);
	return dispatch(gigyaRegister(reqBody))
		.then(() =>
			dispatch(
				analyticsPush({
					event: EVT_REGISTER_SUCCESS,
					loginType: 'e-mail',
					loginMethod: 'password',
				})
			)
		)
		.then(() => dispatch(videolandLoginFlow()))
		.catch((err) => {
			const { register } = getState();

			if (register?.errorCode === EMAIL_ALREADY_EXISTS) {
				dispatch(showNotification(EMAIL_ALREADY_EXISTS_NOTIFICATION));
			}

			// we don't report errors on handled errors
			const HandledErrors = [EMAIL_ALREADY_EXISTS];
			if (!HandledErrors.some((m) => m === register?.errorCode)) {
				reportError(err);
			}

			dispatch(registerError(EVT_REGISTER_ERROR, getState));
			return dispatch(redirectByRegistrationError(err, getState));
		});
};

const gigyaRegister = (payload) => ({
	type: GIGYA_REGISTER,
	payload: {
		client: 'gigya',
		data: {
			api: 'accounts',
			method: 'register',
			params: {
				...payload,
				finalizeRegistration: true,
			},
		},
	},
});

export const completeRegistration = (userData, regToken, snowplowDuid) => (dispatch, getState) => {
	const redirectUrl = getState().authentication.redirectUrl;
	const reqBody = accountInfoBody(userData, regToken, redirectUrl, snowplowDuid);
	// properly save mail if user has chosen not to share email from social register.
	if (reqBody.email) reqBody.profile.email = reqBody.email;

	return dispatch(gigyaSetAccountInfo(reqBody))
		.then(() => dispatch(gigyaFinalizeRegistration(regToken)))
		.then(({ loginProvider }) =>
			dispatch(
				analyticsPush({
					event: EVT_REGISTER_SUCCESS,
					loginType: 'social',
					loginMethod: loginProvider,
				})
			)
		)
		.then(() => dispatch(gigyaGetAccountInfo()))
		.then(() => dispatch(videolandLoginFlow()))
		.then(() => (window.location.href = redirectUrl))
		.catch((err) => {
			reportError(err);
			dispatch(registerError(EVT_COMPLETE_REGISTRATION_ERROR, getState));
			return redirectByRegistrationError(err, getState);
		});
};

const gigyaSetAccountInfo = (userData) => (dispatch) =>
	dispatch({
		type: GIGYA_SET_ACCOUNT_INFO,
		payload: {
			client: 'gigya',
			data: {
				api: 'accounts',
				method: 'setAccountInfo',
				params: userData,
			},
		},
	});

const gigyaFinalizeRegistration = (regToken) => (dispatch) =>
	dispatch({
		type: GIGYA_FINALIZE_REGISTRATION,
		payload: {
			client: 'gigya',
			data: {
				api: 'accounts',
				method: 'finalizeRegistration',
				params: { regToken },
			},
		},
	});

export const gigyaResendVerificationCode =
	({ regToken, email }) =>
	(dispatch) =>
		dispatch({
			type: RESEND_VERIFICATION_CODE,
			payload: {
				client: 'gigya',
				data: {
					api: 'accounts',
					method: 'resendVerificationCode',
					params: { regToken, email },
				},
			},
		}).catch((err) => {
			reportError(err);
			throw err;
		});

export const redirectByRegistrationError = (err, getState) => (dispatch) => {
	const {
		register: { error, errorCode },
		authentication: { redirectUrl },
	} = getState();

	if (!error) return;
	if (errorCode === THIRD_PARTY_COOKIES_BLOCKED) return history.push(ROUTE_THIRD_PARTY_COOKIES);
	if (errorCode === UNVERIFIED_ACCOUNT) return history.push(ROUTE_VERIFY_EMAIL);
	if (errorCode === EMAIL_ALREADY_EXISTS) {
		dispatch(clearRedirectUrl());
		return history.push(
			`${ROUTE_LOGIN}${redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : ``}`
		);
	}

	reportCustomError({
		name: 'redirect_by_registration_error',
		error: errorCode,
		message: 'errorCode is not mapped',
	});
	return history.push(ROUTE_ERROR);
};

function registerError(eventName, getState) {
	const {
		register,
		authentication: { errorCode, gigyaErrorCode, loginProvider },
	} = getState();

	return analyticsPush({
		event: eventName,
		errorCode: register?.errorCode || errorCode,
		gigyaErrorCode: register?.gigyaErrorCode || gigyaErrorCode,
		loginProvider,
	});
}
